import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw, Download, Scale, Lock, ChevronDown, ChevronRight } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// READ-ONLY Term 3 fee reconciliation. Never mutates a transaction, invoice or
// charge. Compares what was charged vs the canonical-engine-correct amount so an
// admin can decide refunds/credits manually.
export default function AdminTerm3Reconciliation() {
  const [terms, setTerms] = useState([]);
  const [selected, setSelected] = useState({});
  const [cutoff, setCutoff] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/academy/terms`, { withCredentials: true });
        // Non-archived terms only — the admin explicitly picks the Term 3 programmes.
        const visible = (r.data || []).filter(
          (t) => !t.is_hidden && !t.is_archived && String(t.status || "").toLowerCase() !== "archived"
        );
        setTerms(visible);
      } catch (err) {
        toast.error("Failed to load terms");
      }
    })();
  }, []);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  const cutoffIso = () => {
    if (!cutoff) return "";
    try { return new Date(cutoff).toISOString(); } catch { return ""; }
  };

  const runReport = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one Term 3 programme");
      return;
    }
    setLoading(true);
    try {
      const params = { term_ids: selectedIds.join(",") };
      const iso = cutoffIso();
      if (iso) params.cutoff = iso;
      const r = await axios.get(`${API}/academy/admin/term3-fee-reconciliation`, {
        params, withCredentials: true,
      });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run report");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    if (selectedIds.length === 0) return;
    try {
      const params = { term_ids: selectedIds.join(","), format: "csv" };
      const iso = cutoffIso();
      if (iso) params.cutoff = iso;
      const r = await axios.get(`${API}/academy/admin/term3-fee-reconciliation`, {
        params, withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "term3_fee_reconciliation.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("CSV export failed");
    }
  };

  const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
  const signedMoney = (v) => {
    if (v == null) return "—";
    const n = Number(v);
    return `${n > 0 ? "+" : ""}$${n.toFixed(2)}`;
  };
  const dirColor = (d) =>
    d === "OVERCHARGED" ? "text-amber-400"
      : d === "UNDERCHARGED" ? "text-red-400"
        : "text-emerald-400";

  const s = data?.summary;

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6" data-testid="term3-recon-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="term3-recon-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Scale className="w-6 h-6 text-cyan-400" /> Term 3 Fee Reconciliation
          </h1>
          <p className="text-slate-400 text-sm mt-1 max-w-3xl">
            Scope: Term 3 only, transactions before the fix; earlier/archived terms excluded.
            Compares what was charged vs the canonical-engine-correct amount.
          </p>
          <div className="mt-2 inline-flex items-center gap-2 text-xs bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 rounded-full px-3 py-1" data-testid="term3-recon-readonly-banner">
            <Lock className="w-3.5 h-3.5" /> Read-only report — nothing is ever changed, charged or refunded here.
          </div>
        </div>

        {/* Controls */}
        <Card className="p-4 bg-slate-900 border-slate-700 space-y-4">
          <div>
            <p className="text-sm font-semibold mb-2">1. Select the Term 3 programmes</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="term3-recon-term-list">
              {terms.map((t) => (
                <label
                  key={t.id}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${
                    selected[t.id] ? "border-cyan-400 bg-cyan-500/10" : "border-slate-700 hover:border-slate-500"
                  }`}
                  data-testid={`term3-recon-term-${t.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[t.id]}
                    onChange={(e) => setSelected((p) => ({ ...p, [t.id]: e.target.checked }))}
                  />
                  <span>{t.name}</span>
                </label>
              ))}
              {terms.length === 0 && <p className="text-slate-500 text-sm">No non-archived terms found.</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div>
              <p className="text-sm font-semibold mb-1">2. Cutoff — canonical-engine deploy moment (your local time)</p>
              <input
                type="datetime-local"
                value={cutoff}
                onChange={(e) => setCutoff(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm"
                data-testid="term3-recon-cutoff"
              />
              <p className="text-xs text-slate-500 mt-1">Leave blank to include all Term 3 transactions regardless of date.</p>
            </div>
            <Button onClick={runReport} disabled={loading} className="bg-cyan-600 hover:bg-cyan-500" data-testid="term3-recon-run">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Run report
            </Button>
            {data && (
              <Button onClick={downloadCsv} variant="outline" data-testid="term3-recon-csv">
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            )}
          </div>
        </Card>

        {/* Summary */}
        {s && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="term3-recon-summary">
            <Card className="p-4 bg-slate-900 border-slate-700">
              <p className="text-xs text-slate-400 uppercase">In scope</p>
              <p className="text-3xl font-bold" data-testid="term3-recon-in-scope">{s.in_scope}</p>
            </Card>
            <Card className="p-4 bg-slate-900 border-red-500/40">
              <p className="text-xs text-slate-400 uppercase">Undercharged</p>
              <p className="text-3xl font-bold text-red-400">{s.undercharged_count}</p>
            </Card>
            <Card className="p-4 bg-slate-900 border-amber-500/40">
              <p className="text-xs text-slate-400 uppercase">Overcharged</p>
              <p className="text-3xl font-bold text-amber-400">{s.overcharged_count}</p>
            </Card>
            <Card className="p-4 bg-slate-900 border-emerald-500/40">
              <p className="text-xs text-slate-400 uppercase">Exactly correct</p>
              <p className="text-3xl font-bold text-emerald-400">{s.exact_count}</p>
            </Card>
            <Card className="p-4 bg-slate-900 border-amber-500/40">
              <p className="text-xs text-slate-400 uppercase">Refund exposure</p>
              <p className="text-2xl font-bold text-amber-400" data-testid="term3-recon-total-over">{money(s.total_overcharged)}</p>
              <p className="text-[10px] text-slate-500">parents overpaid</p>
            </Card>
            <Card className="p-4 bg-slate-900 border-red-500/40">
              <p className="text-xs text-slate-400 uppercase">Club absorption</p>
              <p className="text-2xl font-bold text-red-400" data-testid="term3-recon-total-under">{money(s.total_undercharged)}</p>
              <p className="text-[10px] text-slate-500">under-collected</p>
            </Card>
          </div>
        )}

        {s && s.needs_review_count > 0 && (
          <div className="text-sm bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded p-3" data-testid="term3-recon-review-note">
            {s.needs_review_count} transaction(s) flagged “Insufficient data — needs manual review”. Their base could not be independently reproduced from stored inputs; the total shown corrects the recorded card fee only.
          </div>
        )}

        {/* Table */}
        {data && (
          <Card className="bg-slate-900 border-slate-700 overflow-x-auto">
            <table className="w-full text-sm" data-testid="term3-recon-table">
              <thead className="text-slate-400 border-b border-slate-700">
                <tr className="text-left">
                  <th className="p-3"></th>
                  <th className="p-3">Reference</th>
                  <th className="p-3">Parent / Children</th>
                  <th className="p-3">Program</th>
                  <th className="p-3">Method</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Charged</th>
                  <th className="p-3 text-right">Correct</th>
                  <th className="p-3 text-right">Delta</th>
                  <th className="p-3">Direction</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <>
                    <tr key={r.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`term3-recon-row-${r.id}`}>
                      <td className="p-3">
                        <button onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))} data-testid={`term3-recon-expand-${r.id}`}>
                          {expanded[r.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {r.payment_reference || r.id?.slice(0, 8)}
                        {r.needs_review && <span className="ml-1 text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">review</span>}
                      </td>
                      <td className="p-3">
                        <div>{r.parent_name || r.parent_email || "—"}</div>
                        <div className="text-xs text-slate-500">{r.children || "—"}</div>
                      </td>
                      <td className="p-3 text-xs">{r.term_name || "—"}</td>
                      <td className="p-3">{r.payment_method}</td>
                      <td className="p-3 text-xs">{r.payment_status}</td>
                      <td className="p-3 text-right">{money(r.charged)}</td>
                      <td className="p-3 text-right">{money(r.correct)}</td>
                      <td className={`p-3 text-right font-semibold ${dirColor(r.direction)}`}>{signedMoney(r.delta)}</td>
                      <td className={`p-3 font-semibold ${dirColor(r.direction)}`}>{r.direction}</td>
                    </tr>
                    {expanded[r.id] && (
                      <tr className="bg-slate-950/60" data-testid={`term3-recon-detail-${r.id}`}>
                        <td></td>
                        <td colSpan={9} className="p-4">
                          {r.review_reason && (
                            <p className="text-xs text-amber-300 mb-3">⚠ {r.review_reason}</p>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Charged (as invoiced)</p>
                              <table className="w-full text-xs">
                                <tbody>
                                  {(r.charged_line_items || []).map((li, i) => (
                                    <tr key={i} className="border-b border-slate-800">
                                      <td className="py-1">{li.label}</td>
                                      <td className="py-1 text-right">{money(li.amount)}</td>
                                    </tr>
                                  ))}
                                  <tr className="font-semibold">
                                    <td className="py-1">Total charged</td>
                                    <td className="py-1 text-right">{money(r.charged)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-cyan-400 uppercase mb-2">Correct (canonical engine)</p>
                              <table className="w-full text-xs">
                                <tbody>
                                  {(r.correct_line_items || []).length === 0 ? (
                                    <tr><td className="py-1 text-slate-500">Breakdown not available (see review note)</td></tr>
                                  ) : (r.correct_line_items).map((li, i) => (
                                    <tr key={i} className="border-b border-slate-800">
                                      <td className="py-1">{li.label}</td>
                                      <td className="py-1 text-right">{money(li.amount)}</td>
                                    </tr>
                                  ))}
                                  <tr className="font-semibold">
                                    <td className="py-1">Total correct</td>
                                    <td className="py-1 text-right">{money(r.correct)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={10} className="p-6 text-center text-slate-500">No transactions in scope for the selected terms / cutoff.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
